/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import AccountModel from 'cio/models/account';
import CouponModel from 'cio/models/coupon';
import PlanModel, { PlanType } from 'cio/models/plan';
import SubscriptionModel from 'cio/models/subscription';
import TierModel from 'cio/models/tier';
import { inject as service } from '@ember/service';
import BillingService, { MIN_ZIP_LENGTH, ZIP_INPUT_DEBOUNCE } from 'cio/services/billing';
import { tracked } from '@glimmer/tracking';
import AnalyticsService from 'cio/services/analytics';
import ApiService from 'cio/services/api';
import { Plans } from 'cio/utils/billing/plans';
import { isEmpty } from '@ember/utils';
import { task, timeout } from 'ember-concurrency';
import MomentService from 'cio/services/moment';
import AccountService from 'cio/services/account';
import StripeV3Service from 'ember-stripe-elements/services/stripev3';
import DS from 'ember-data';
import { action } from '@ember/object';

interface SignupFormArgs {
  account: AccountModel;
  subscription: SubscriptionModel;
  plan: PlanModel;
  couponCode?: string;
  coupon?: CouponModel;
  taxRate: number;
  tiers: TierModel[];
  showCheckout: boolean;
  startSubscription: () => unknown;
  onBackToPlans: () => unknown;
  total: number;
  canCancel?: boolean;
  cancelActionLabel?: string | null;
}

export default class SignupForm extends Component<SignupFormArgs> {
  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service billing!: BillingService;
  @service moment!: MomentService;
  @service account!: AccountService;
  @service('stripev3') stripe!: StripeV3Service;
  @service store!: DS.Store;

  @tracked showCheckout = false;
  @tracked showDowngradeModal = false;
  @tracked showPinnedPlanModal = false;
  @tracked customerCount = 0;
  @tracked objectCount = 0;

  get customerOverage() {
    return this.billing.billingInfo?.billingEstimates?.customer_overage ?? 0;
  }

  get objectOverage() {
    return this.billing.billingInfo?.billingEstimates?.object_overage ?? 0;
  }

  plans = [Plans.Essentials, Plans.Premium, Plans.Enterprise];
  planType = PlanType;

  get planPrice() {
    let { plan } = this.args;
    let planPrice = plan.annual ? plan.annualPrice : plan.price;
    let totalPlanPrice = planPrice + (plan.cdpPremiumPrice ?? 0);

    return totalPlanPrice < 0 ? 0 : totalPlanPrice;
  }

  get discount() {
    let {
      planPrice,
      args: { coupon },
    } = this;

    let discount = 0;

    if (!coupon) {
      return discount;
    }

    if (coupon.amountOff) {
      discount = coupon.amountOff;
    } else if (coupon.percentOff) {
      discount = planPrice * (coupon.percentOff / 100);
    }

    return discount;
  }

  get subtotal() {
    let { planPrice, discount } = this;

    planPrice -= discount;

    if (planPrice < 0) {
      planPrice = 0;
    }

    return planPrice;
  }

  get tax() {
    return this.args.taxRate ? this.subtotal * this.args.taxRate : undefined;
  }

  get total() {
    let usage = 0;
    if (this.args.plan.planType === Plans.Essentials.planType) {
      usage += this.customerOverage;
      usage += this.objectOverage;
    }
    return {
      amount: this.subtotal + (this.tax ?? 0) + usage,
      taxIncluded: this.tax != null,
    };
  }

  get pinnedPlanUsageInDollars() {
    return Number(this.args.plan.meteredCustomerRate) * 1000;
  }

  get pinnedPlanObjectsUsageInDollars() {
    return Number(this.args.plan.meteredObjectRate) * 1000;
  }

  get showOverages() {
    return this.objectOverage > 0 || this.customerOverage > 0;
  }

  @action
  async openModal() {
    this.showPinnedPlanModal = true;
    let { account } = this.args;
    let { currentUser } = account;
    let data = {
      account_id: account.id,
      requester: currentUser.fullName,
      requester_email: currentUser.email,
      requester_user_id: currentUser.id,
    };
    this.analytics.track('plan_billing', 'included_in_plan_viewed', { data });
  }

  getUpdatedTaxRateTask = task(
    { restartable: true },
    async ({ country, state, zipCode }: Record<string, string>, debounce: boolean) => {
      if ([country, state, zipCode].some(isEmpty) || zipCode.length < MIN_ZIP_LENGTH) {
        return undefined;
      }

      if (debounce) {
        await timeout(ZIP_INPUT_DEBOUNCE);
      }

      return this.billing.updateTaxRate.perform({ country, state, zipCode });
    },
  );

  calculateUsage = task({ drop: true }, async () => {
    let accountUsage = await this.store.queryRecord('account-usage', {});

    let { customersTotal, objectsTotal } = await accountUsage;
    this.customerCount = customersTotal;
    this.objectCount = objectsTotal;
  });

  constructor(owner: unknown, args: SignupFormArgs) {
    super(owner, args);
    this.calculateUsage.perform();

    // If a coupon has been applied, show the checkout form.
    this.showCheckout = this.args.coupon != null;
  }
}
